import React from 'react';

/**
 * Kolega design-system — Button
 * Lime "primary" for the single most important action on a view;
 * dark/secondary for everything else. Theme-aware via design tokens.
 */
export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'accent-outline';
export type ButtonSize = 'sm' | 'md' | 'lg';

const SIZES: Record<ButtonSize, { padding: string; fontSize: number; gap: number }> = {
  sm: { padding: '7px 13px', fontSize: 13, gap: 7 },
  md: { padding: '10px 16px', fontSize: 13.5, gap: 7 },
  lg: { padding: '13px 22px', fontSize: 14.5, gap: 9 },
};

const VARIANTS: Record<ButtonVariant, React.CSSProperties> = {
  primary: { background: 'var(--lime)', color: 'var(--lime-fg)', border: '1px solid transparent', fontWeight: 700 },
  secondary: { background: 'var(--surface-3)', color: 'var(--text-strong)', border: '1px solid var(--border)', fontWeight: 600 },
  ghost: { background: 'transparent', color: 'var(--text-secondary)', border: '1px solid transparent', fontWeight: 600 },
  'accent-outline': { background: 'var(--surface-3)', color: 'var(--accent-ink)', border: '1px solid var(--lime-soft-border)', fontWeight: 700 },
};

export interface ButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'style'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: React.ReactNode;
  iconRight?: React.ReactNode;
  fullWidth?: boolean;
  style?: React.CSSProperties;
}

export function Button({
  children,
  variant = 'primary',
  size = 'md',
  icon,
  iconRight,
  fullWidth = false,
  disabled = false,
  style,
  ...rest
}: ButtonProps) {
  const s = SIZES[size] || SIZES.md;
  const v = VARIANTS[variant] || VARIANTS.primary;
  return (
    <button
      type="button"
      disabled={disabled}
      style={{
        display: fullWidth ? 'flex' : 'inline-flex',
        width: fullWidth ? '100%' : undefined,
        alignItems: 'center',
        justifyContent: 'center',
        gap: s.gap,
        padding: s.padding,
        fontSize: s.fontSize,
        fontFamily: 'var(--font-sans)',
        lineHeight: 1,
        borderRadius: 'var(--radius-md)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        whiteSpace: 'nowrap',
        transition: 'filter var(--dur-fast) var(--ease-out)',
        ...v,
        ...style,
      }}
      {...rest}
    >
      {icon}
      {children}
      {iconRight}
    </button>
  );
}

export default Button;
